import { Remark } from 'react-remark'
import React from 'react'

import {
  ClientUser,
  CompleteMark,
  formatDate,
  formatDateEx,
  formatWithThousandSep,
  showPaymentWreserve,
} from '../utils/utils'
import { formatDocumentTitle } from '../utils/documentTitleUtils'
import { NvvAction } from '../enums/DocActions'
import { Additional, Payment } from '../interfaces/Reservation'
import { useFeatureFlag } from '../hooks/useFeatureFlag'

import DocNotes from './DocNotes'
import { DocHeader, DocHeaderType } from './DocHeader'
import { OrganizationFrame, OrganizationFrameType } from './OrganizationFrame'
import { ClientFrame, ClientFrameType } from './ClientFrame'
import { VehicleFrame, VehicleFrameType } from './VehicleFrame'
import { ReservationDet } from './ReservationPreview'
import { DocStatusHeaderEx } from './DocStatusHeader'
import { PayVehicle } from './ReservationNvvEdit'
import { TotalDisplay } from './SalenoteDetail'
import ReservationNvvDetailV2, {
  GridVeh as GridVehV2,
  GridPayment as GridPaymentV2,
  GridCost as GridCostV2,
} from './ReservationNvvDetailV2'

const ReservationNvvDetail: React.FC<{
  reservation: ReservationDet
  nvvInd: number
}> = ({ reservation, nvvInd }) => {
  const { useNewDocumentUI } = useFeatureFlag()

  if (useNewDocumentUI) return <ReservationNvvDetailV2 reservation={reservation} nvvInd={nvvInd} />

  const archivedAt = reservation.archivedAt
  const voidedAt = reservation.voidedAt
  let docHeader: DocHeaderType

  if (nvvInd === NvvAction.SoloReserva) {
    const myNumberRes = reservation?.number === 0 ? 'XX' : reservation?.number

    // Format title for reservation using template configuration
    const defaultReservationTitle = 'Documento de Reserva'
    const formattedReservationTitle = formatDocumentTitle(reservation.titleConfig, defaultReservationTitle, myNumberRes)

    docHeader = {
      urlImage: reservation.body.organization.pictures.logotype,
      titulo: formattedReservationTitle,
      isAnulada: !!voidedAt,
      tituloAnulada: 'NULA',
      fechaIniText: `Fecha`,
      fechaIniValue: formatDate(reservation?.date),
      fechaFinText: '',
      fechaFinValue: '',
    }
  } else {
    const myNumberNvv = reservation?.number === 0 ? 'XX' : reservation?.number

    // Format title for sale note using template configuration
    const defaultSaleNoteTitle = 'Nota de Venta'
    const formattedSaleNoteTitle = formatDocumentTitle(reservation.titleConfig, defaultSaleNoteTitle, myNumberNvv)

    docHeader = {
      urlImage: reservation.body.organization.pictures.logotype,
      titulo: formattedSaleNoteTitle,
      isAnulada: !!voidedAt,
      tituloAnulada: 'NULA',
      fechaIniText: `Fecha`,
      fechaIniValue: formatDate(reservation?.date),
      fechaFinText: '',
      fechaFinValue: '',
    }
  }

  const user = { name: reservation?.profile_name ?? '', phone: reservation.user_phone ?? '' }
  const organizationFrame: OrganizationFrameType = {
    ...reservation.body.organization,
    user: user,
  }
  const clientFrame: ClientFrameType = {
    ...reservation.body.client,
  }

  const vehicleFrame: VehicleFrameType = {
    ...reservation.body.vehicle,
    price: reservation.price,
  }

  const costs = reservation.additionals ?? []
  const payments = reservation.payments ?? []
  const vehicles = reservation.vehicles ?? []

  const clientUser: ClientUser = {
    client: {
      firstname: reservation.body.client.name,
      lastname: '',
      nin: reservation.body.client.nin,
    },
  }

  const footer = CompleteMark(reservation.footer ?? '', clientUser)

  const price = reservation.price
  const porReservar = costs.reduce((accumulator, item) => Number(accumulator) + item.cost, price)
  const reservado =
    payments.reduce((accumulator, item) => Number(accumulator) + item.value, 0) +
    vehicles.reduce((accumulator, item) => Number(accumulator) + item.price, 0)
  const saldo = reservado - porReservar

  let title = ''
  let date = ''
  let by = ''
  let cause = ''

  if (voidedAt) {
    title = 'Esta reserva fue anulada'
    date = voidedAt ?? ''
    cause = reservation.voidedCause ?? ''
    by = reservation?.voidedBy_user ?? ''
  } else if (archivedAt) {
    title = 'Esta reserva fue archivada'
    date = archivedAt ?? ''
    by = reservation?.archivedBy_user ?? ''
  }

  return (
    <>
      <DocStatusHeaderEx title={title} date={date} cause={cause} by={by} />
      <DocHeader header={docHeader} />
      <hr className="w-full border-t border-black-500 my-2" />

      <OrganizationFrame organization={organizationFrame} />

      <hr className="w-full border-t border-black-500 my-2" />
      <ClientFrame client={clientFrame} />

      <hr className="w-full border-t border-black-500 my-2" />
      <VehicleFrame vehicle={vehicleFrame} />

      <div className="border-2 border-black pl-2 mt-2">
        <GridCost title="Adicionales" paycosts={costs} />
      </div>
      <TotalDisplay title="Total a pagar" total={porReservar} />
      <div className="border-2 border-black pl-2 mt-2">
        <GridPayment title="Pagos" payments={payments} />
      </div>
      <div className="border-2 border-black pl-2 mt-2">
        <GridVeh title="Vehiculos" paycosts={vehicles} />
      </div>
      <TotalDisplay title="Total pagos" total={reservado} />
      {saldo > 0 && <TotalDisplay title="Saldo a favor del cliente" total={saldo} name="saldo" />}
      {saldo < 0 && <TotalDisplay title="Saldo a pagar" total={-1 * saldo} name="saldo" />}

      <DocNotes notes={reservation?.notes} />
      <hr className="w-full border-t border-black-500 my-2" />
      <Remark>{footer}</Remark>
    </>
  )
}

const showDescription = (pay: PayVehicle) => {
  return pay.description
}

export const GridVeh: React.FC<{
  title: string
  paycosts: PayVehicle[]
  showPurchaseLinks?: boolean
  isApproved?: boolean
}> = ({ title, paycosts, showPurchaseLinks = false, isApproved = false }) => {
  const { useNewDocumentUI } = useFeatureFlag()

  if (useNewDocumentUI)
    return <GridVehV2 title={title} paycosts={paycosts} showPurchaseLinks={showPurchaseLinks} isApproved={isApproved} />

  return (
    <>
      <div className="text-[1.5em] mb-2">{title}</div>
      <div className="grid grid-cols-4">
        {paycosts.map((payment) => (
          <React.Fragment key={payment.key}>
            <div className="pr-4 col-span-2">
              <div>{showDescription(payment)}</div>
              {showPurchaseLinks && isApproved && payment.purchaseCreated && payment.associatedPurchaseId && (
                <div className="text-sm text-blue-600 mt-1 print:hidden">
                  <a href={`/purchases/view/${payment.associatedPurchaseId}`} className="hover:text-blue-800 underline">
                    Ver Compra #{payment.purchaseNumber || 'Auto-generada'} →
                  </a>
                </div>
              )}
              {showPurchaseLinks && isApproved && !payment.purchaseCreated && (
                <div className="text-sm text-amber-600 mt-1 print:hidden">Compra pendiente de crear</div>
              )}
            </div>

            <div></div>

            <div className="pr-4 text-right">${formatWithThousandSep(payment.price)}</div>
          </React.Fragment>
        ))}
      </div>
    </>
  )
}
export const GridPayment: React.FC<{ title: string; payments: Payment[] }> = ({ title, payments }) => {
  const { useNewDocumentUI } = useFeatureFlag()

  if (useNewDocumentUI) return <GridPaymentV2 title={title} payments={payments} />

  return (
    <>
      <div className="text-[1.5em] mb-2">{title}</div>
      <div className="grid grid-cols-4">
        {payments.map((payment) => (
          <React.Fragment key={payment._id}>
            <div className="pr-4 col-span-2">{showPaymentWreserve(payment)}</div>
            <div className="pr-4">{formatDateEx(payment.date)}</div>
            <div className="pr-4 text-right">${formatWithThousandSep(payment.value)}</div>
          </React.Fragment>
        ))}
      </div>
    </>
  )
}
export const GridCost: React.FC<{ title: string; paycosts: Additional[] }> = ({ title, paycosts }) => {
  const { useNewDocumentUI } = useFeatureFlag()

  if (useNewDocumentUI) return <GridCostV2 title={title} paycosts={paycosts} />

  return (
    <>
      <div className="text-[1.5em] mb-2">{title}</div>
      <div className="grid grid-cols-4">
        {paycosts.map((cost) => (
          <React.Fragment key={cost._id}>
            <div className="pr-4 col-span-2">{cost.description}</div>
            <div className="pr-4">{formatDateEx(cost.date)}</div>
            <div className="pr-4 text-right">${formatWithThousandSep(cost.cost)}</div>
          </React.Fragment>
        ))}
      </div>
    </>
  )
}

export default ReservationNvvDetail
